import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multi-select";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/searchable-select";
import { cn } from "@/lib/utils";
import type { TaskPriority } from "@/services/email-to-task.service";
import type { DraftGroup, DraftSubtask } from "@/services/inbound-email.service";
import { textareaClass } from "@/components/workspace-settings/email-to-task/fields";
import { DraftIssues } from "./DraftIssues";
import { DraftSubtaskRow } from "./DraftSubtaskRow";

const PRIORITY_OPTIONS: SearchableSelectOption[] = [
	{ value: "low", label: "Low" },
	{ value: "medium", label: "Medium" },
	{ value: "high", label: "High" },
	{ value: "urgent", label: "Urgent" },
];

export function DraftGroupCard({
	group,
	index,
	userOptions,
	projectOptions,
	editable,
	onChange,
	onSubtaskChange,
}: {
	group: DraftGroup;
	index: number;
	userOptions: SearchableSelectOption[];
	projectOptions: SearchableSelectOption[];
	editable: boolean;
	onChange: (patch: Partial<DraftGroup>) => void;
	onSubtaskChange: (si: number, patch: Partial<DraftSubtask>) => void;
}) {
	const disabled = !editable || !group.include;

	return (
		<section
			aria-label={`Task ${index + 1}`}
			className={cn("rounded-xl border border-border bg-surface p-4", !group.include && "opacity-60")}
		>
			<div className="flex items-start gap-3">
				<Checkbox
					className="mt-2.5"
					checked={group.include}
					onCheckedChange={(v) => onChange({ include: v === true })}
					disabled={!editable}
					aria-label={`Create task ${group.title}`}
				/>
				<div className="min-w-0 flex-1 space-y-3">
					<Input
						value={group.title}
						onChange={(e) => onChange({ title: e.target.value })}
						disabled={disabled}
						className="font-medium"
						aria-label="Task title"
					/>
					<div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
						<SearchableSelect
							value={group.project_id}
							onValueChange={(v) => onChange({ project_id: v || null })}
							options={projectOptions}
							placeholder="Project"
							size="sm"
							disabled={disabled}
							ariaLabel="Project"
						/>
						<MultiSelect
							value={group.assignee_ids}
							onValueChange={(ids) => onChange({ assignee_ids: ids })}
							options={userOptions}
							placeholder="Unassigned"
							size="sm"
							disabled={disabled}
							ariaLabel="Assignees"
						/>
						<SearchableSelect
							value={group.priority}
							onValueChange={(v) => onChange({ priority: (v || null) as TaskPriority | null })}
							options={PRIORITY_OPTIONS}
							placeholder="Default priority"
							size="sm"
							clearable
							disabled={disabled}
							ariaLabel="Priority"
						/>
						<Input
							type="date"
							value={group.due_date ?? ""}
							onChange={(e) => onChange({ due_date: e.target.value || null })}
							disabled={disabled}
							aria-label="Due date"
						/>
					</div>
					{(group.description || (editable && group.include)) && (
						<textarea
							rows={2}
							className={cn(textareaClass, "text-xs")}
							placeholder="Description (optional)"
							value={group.description ?? ""}
							onChange={(e) => onChange({ description: e.target.value || null })}
							disabled={disabled}
							aria-label="Task description"
						/>
					)}
					<DraftIssues unresolved={group.unresolved_names} warnings={group.warnings} />

					{group.subtasks.length > 0 && (
						<ul className="space-y-2" aria-label="Subtasks">
							{group.subtasks.map((s, si) => (
								<DraftSubtaskRow
									key={si}
									subtask={s}
									userOptions={userOptions}
									editable={editable}
									parentIncluded={group.include}
									onChange={(patch) => onSubtaskChange(si, patch)}
								/>
							))}
						</ul>
					)}
				</div>
			</div>
		</section>
	);
}
